import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Icons as GridIcons,
  Column,
  GeneralActions,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { Color, Colors } from "../../../../../../components/Form/Button";
import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import ConfirmChangeStatus from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Country } from "../../../../../../models/country";
import { Office } from "../../../../../../models/office";
import { Port } from "../../../../../../models/port";
import { Row } from "../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import OfficeManageModal from "./components/ManageModal";
import { OfficePageProvider } from "./context/Office";
import { useActions } from "./hooks/useAction";
import { BadgeActive, BadgeInactive, ChangeAllStatusButton, FiEyeButton, Wrapper } from "./styles";
import { isShowAction } from "Util/permissions";

const hideSort = true;
const hideSearch = true;

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

const OfficesPageComponent: React.FC<Props> = ({ permission }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    variables,
    setOffice,

    createOffice,
    updateOffice,

    setHasErrors,

    setCurrentPage,
    setPageSize,
    handleSearch,
    handleFilter,
    handleSort,
  } = useActions();

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => {
    if (status === Status.ACTIVE) {
      return <BadgeActive>{status}</BadgeActive>;
    }

    return <BadgeInactive>{status}</BadgeInactive>;
  };

  const editOffice = (index: number) => {
    if (variables.hasErrors !== undefined) setHasErrors(undefined);
    let officeList: Office[] = [];

    try {
      officeList = JSON.parse(JSON.stringify(variables.offices));
    } catch {
      officeList = [...variables.offices];
    }
    const find = officeList.find((branch) => Number(branch.id) === Number(index));

    if (find) {
      setOffice(find);
      setIsOnViewMode(false);
      setModalOpened(true);
    }
  };

  const viewOffice = (index: number) => (
    <Tooltip title="View" placement="top">
      <div>
        <FiEyeButton
          size={18}
          onClick={() => {
            if (variables.hasErrors !== undefined) setHasErrors(undefined);
            const find = variables.offices.find((office) => Number(office.id) === Number(index));

            if (find) {
              setOffice(find);
              setIsOnViewMode(true);
              setModalOpened(true);
            }
          }}
        />
      </div>
    </Tooltip>
  );

  const changeOfficeStatus = (index: number) => {
    const find = variables.offices.find((office) => Number(office.id) === Number(index));

    if (find) {
      updateOffice({
        id: Number(index),
        office: {
          ...find,
          status: find.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0) {
      return (
        <ChangeAllStatusButton
          tooltip={translate("changeStatus")}
          data-cy="inactiveStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={17} className="mr-2" />
          {translate("changeStatus")}
        </ChangeAllStatusButton>
      );
    }

    return null;
  };

  const handleSaveOfficeSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, variables.offices);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "beCode", title: translate("beCode"), searchIdentifier: "be_code" },
    { name: "name", title: translate("name"), formatText: (value) => value },
    {
      name: "ports",
      title: translate("ports"),
      hideSort,
      hideSearch,
      formatComponent: (value?: Port[]) => {
        const ports = value || [];

        if (!ports?.length) return 0;

        return (
          <Tooltip title={ports?.map((port) => port.siteCode)?.join(", ")}>
            <span>{(value || []).length}</span>
          </Tooltip>
        );
      },
    },
    {
      name: "country",
      title: translate("country"),
      hideSearch,
      hideSort,
      formatComponent: (value?: Country) => value?.name || "",
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleActive", "handleNewRecord"],
    },
    handleNewRecord:
      permission && isShowAction(currentUser, permission?.create)
        ? {
            text: translate("create"),
            action: () => {
              if (variables.hasErrors !== undefined) setHasErrors(undefined);
              setIsOnViewMode(false);
              setOffice(undefined);
              setModalOpened(true);
            },
            tooltip: translate("create"),
          }
        : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("beCode"),
          column: "beCode",
        },
        {
          name: translate("name"),
          column: "name",
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    handleActive: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (variables.hasErrors !== undefined && !variables.hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.message.offices.id",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [variables.hasErrors]);

  return (
    <Wrapper data-cy="branchDataList">
      {/* <header className="mb-4">
        <Breadcrumb
          items={[
            { title: translate("configurations") },
            {
              title: translate("offices"),
              color: "#465869",
              dataCy: "breadcrumbsSubTitle",
            },
          ]}
        />
      </header> */}

      <ShipeeziNewGrid
        data={variables.offices}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isFetching || variables.isCreating || variables.isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={variables.pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={variables.page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveOfficeSelected(rows)}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit:
            permission && isShowAction(currentUser, permission?.edit)
              ? (officeId) => editOffice(Number(officeId))
              : undefined,
          handleChange: (officeId) => changeOfficeStatus(Number(officeId)),
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewOffice }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      {modalOpened && (
        <OfficeManageModal
          officeToUpdate={variables.office}
          isOpened={modalOpened}
          isLoading={variables.isUpdating || variables.isCreating}
          isView={isOnViewMode}
          onCloseModal={() => setModalOpened(false)}
          onSubmit={(office: Office) => {
            if (office.id) {
              updateOffice(
                { id: office.id, office },
                {
                  onSuccess: () => {
                    setModalOpened(false);
                  },
                }
              );
            } else
              createOffice(
                {
                  ...office,
                  companyId: currentUser?.companyId,
                },
                {
                  onSuccess: () => {
                    setModalOpened(false);
                  },
                }
              );
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          closeModal={() => setOpenConfirmation(false)}
          rows={rows}
          isLoading={variables.isUpdating}
          hasErrors={variables.hasErrors}
          update={(rowId, status) => {
            if (rowId) {
              updateOffice({ id: rowId, office: { status } as Office });
              setClearSelection(true);
            }
          }}
        />
      )}
    </Wrapper>
  );
};

const OfficesPage = ({ permission }: Props) => {
  return (
    <OfficePageProvider>
      <OfficesPageComponent permission={permission} />
    </OfficePageProvider>
  );
};

export default OfficesPage;
